import {
    computed,
    inject,
    Injectable,
    Injector,
    signal
} from '@angular/core';

import {
    validate as validateUuid,
    v4 as generateUuid
} from 'uuid';

import {
    EnvironmentConfig,
    EnvironmentType
} from 'src/environments/environment-config';

import {
    CordovaNetworkInterfaceAddress
} from 'app/types';

import {
    UserActiveSessionManagerService
} from './user-active-session-manager.service';

type UserAgentOs = 'Android' | 'iOS' | 'Windows' | 'macOS' | 'Linux' | 'Unknown';

type DeviceMeta = {
    client_serial: string;
    client_uuid: string;
    client_os: string;
    device_name?: string;
}

type NetworkInformation = {
    client_lan_ip: string;
    client_lan_subnet: string;
}

const backendEnvMap: Record<EnvironmentType, 'local' | 'prod' | 'beta'> = {
    'beta': 'beta',
    'local': 'local',
    'stable': 'prod',
    'unstable': 'beta'
};

@Injectable({
    providedIn: 'root'
})
export class EnvironmentInfoService {
    private readonly injector = inject(Injector);

    private _isAndroid = signal(false);
    private _isAppleMobile = signal(false);
    private _isElectronApp = signal(false);
    private _isMobileApp = signal(false);

    private _baseUrl: string;
    private _deviceMeta?: DeviceMeta;

    private _sessionId = this.initSessionId();
    private _userAgentOS = signal(this.setUserAgentFromNavigator());
    private netInfoPromise?: Promise<NetworkInformation>;

    private initSessionId(): string {
        const localStorage = window.localStorage;
        let deviceId;

        if (localStorage) {
            deviceId = window.localStorage.getItem("scl-device-id");
        }

        if (!deviceId || !validateUuid(deviceId)) {
            deviceId = generateUuid();

            if (localStorage) {
                window.localStorage.setItem("scl-device-id", deviceId);
            }
        }

        return deviceId;
    }

    private setUserAgentFromNavigator(): UserAgentOs {
        const userAgent = window.navigator.userAgent;

        //Obtain os info from the user agent
        if (userAgent.match(/(Android)/)) {
            return 'Android';
        } else if (userAgent.match(/(iPhone|iPod|iPad)/)) {
            return 'iOS';
        } else if (userAgent.match(/(Windows)/)) {
            return 'Windows';
        } else if (userAgent.match(/(Macintosh)/)) {
            return 'macOS';
        } else if (userAgent.match(/(X11)/)) {
            return 'Linux';
        }

        return 'Unknown';
    }

    constructor() {
        switch (EnvironmentConfig.clientId) {
            case 'Scloby3PG':
                this._isMobileApp.set(true);

                switch (window.device.manufacturer) {
                    case 'Apple':
                        this._isAppleMobile.set(true);
                        break;
                    default:
                        this._isAndroid.set(true);
                        break;
                }
                break;
            case 'SclobyElectron3':
                this._isElectronApp.set(true);
                break;
            default:
                if (window.require) {
                    this._isElectronApp.set(true);

                    Object.assign(EnvironmentConfig, {
                        redirectUri: "http://ok.login.tilby.com/"
                    });
                }
                break;
        }

        if (this._isElectronApp()) {
            this._baseUrl = "./";
        } else if (this._isMobileApp()) {
            this._baseUrl = document.URL.substring(0, document.URL.lastIndexOf("/")).replace(/.*?:\/\//g, "") + "/";
        } else {
            this._baseUrl = "/";
        }
    }

    /**
     * Returns the base URL of the application based on the current environment.
     *
     * @return {string} The base URL of the application.
     */
    public getBaseUrl(): string {
        return this._baseUrl;
    }

    private async getElectronNetworkInfo(): Promise<NetworkInformation> {
        const sysinfo = window.require('systeminformation');

        const defInterface = await sysinfo.networkInterfaceDefault();
        const interfaces: any[] = await sysinfo.networkInterfaces();
        const targetIface = interfaces.find((i) => (i.iface === defInterface));

        if (!targetIface) {
            throw 'INTERFACE_NOT_FOUND';
        }

        return {
            client_lan_ip: targetIface.ip4,
            client_lan_subnet: targetIface.ip4subnet
        };
    }

    private async getMobileNetworkInfo(): Promise<NetworkInformation> {
        if(!window.networkinterface) {
            throw 'INTERFACE_NOT_FOUND';
        }

        // Attempt resolve using WiFi interface
        let netInfo: CordovaNetworkInterfaceAddress | undefined = await new Promise((resolve) => window.networkinterface!.getWiFiIPAddress((result) => resolve(result), () => resolve(undefined)));

        // Attempt resolve using carrier interface
        if (!netInfo?.ip) {
            netInfo = await new Promise((resolve) => window.networkinterface!.getCarrierIPAddress((result) => resolve(result), () => resolve(undefined)));
        }

        if (!netInfo?.ip) {
            throw 'INTERFACE_NOT_FOUND';
        }

        return {
            client_lan_ip: netInfo.ip,
            client_lan_subnet: netInfo.subnet
        };
    }

    /**
     * Retrieves the local IP address of the device.
     * 
     * @return {Promise<NetworkInformation>} A promise that resolves with an object containing the local IP address of the device.
     * 
     * @throws {string} If the interface is not found or if the platform is invalid.
     */
    public getNetworkInfo(): Promise<NetworkInformation> {
        if (!this.netInfoPromise) {
            if (this._isElectronApp()) {
                this.netInfoPromise = this.getElectronNetworkInfo();
            } else if (this._isMobileApp()) {
                this.netInfoPromise = this.getMobileNetworkInfo();
            } else {
                this.netInfoPromise = Promise.reject('INVALID_PLATFORM');
            }

            // Remove cached net information after 15 seconds
            setTimeout(() => {
                this.netInfoPromise = undefined;
            }, 15000);
        }

        return this.netInfoPromise;
    }

    /**
     * Checks if the app is running on an Android device
     */
    public isAndroid = computed(() => this._isAndroid());

    /**
     * Checks if the app is running on an Apple mobile device (iPhone or iPad)
     */
    public isAppleMobile = computed(() => this._isAppleMobile());

    /**
     * Checks if the app is running on a mobile app (Android or iOS)
     */
    public isMobileApp = computed(() => this._isMobileApp());

    /**
     * Checks if the app is running on an Electron app (desktop)
     */
    public isElectronApp = computed(() => this._isElectronApp());

    /**
     * Checks if the app is running on a web app (browser)
     */
    public isWebApp = computed(() => !this._isMobileApp() && !this._isElectronApp());

    /**
     * Checks if TCP sockets can be used
     */
    public canUseTcpSockets = computed(() => this._isMobileApp() || this._isElectronApp());

    /**
     * Checks if serial ports can be used
     */
    public canUseSerialPorts = computed(() => this._isElectronApp());

    /**
     * Checks if the camera barcode reader is supported
     */
    public isCameraBarcodeSupported = computed(() => this._isMobileApp());

    /**
     * Checks if external links can be opened
     */
    public canOpenExternalLinks = computed(() => true);

    /**
     * Checks if files can be downloaded
     */
    public canDownloadFiles = computed(() => !this._isMobileApp());

    /**
     * Checks if the network can be scanned
     */
    public canScanNetwork = computed(() => this._isMobileApp() || this._isElectronApp());

    /**
     * Checks if files can be shared
     */
    public canShare = computed(() => this._isMobileApp());

    /**
     * Checks if printing is supported
     */
    public canPrint = computed(() => this._isElectronApp() || !this._isMobileApp());

    /**
     * Checks if the login view should be used
     */
    public usesLoginView = computed(() => this._isMobileApp() || this._isElectronApp());

    /**
     * Gets the OS of the user agent
     */
    public getUserAgentOS = computed(() => this._userAgentOS());

    /**
     * Checks if the app is running on Windows
     */
    public isWindows = computed(() => this._userAgentOS() === 'Windows');

    /**
     * Checks if the app is running on macOS
     */
    public isMacOS = computed(() => this._userAgentOS() === 'macOS');

    /**
     * Returns the device meta, which contains the client serial, client UUID, and client OS.
     * @returns {DeviceMeta} the device meta
     */
    public async getDeviceMeta(): Promise<DeviceMeta> {
        if(!this._deviceMeta) {
            const deviceMeta = {
                client_serial: '',
                client_uuid: '',
                client_os: ''
            };

            if(this.isElectronApp()) {
                const sysinfo = window.require('systeminformation');
    
                try {
                    const systemInfo = await sysinfo.system();

                    deviceMeta.client_serial = systemInfo.serial;
                    deviceMeta.client_uuid = systemInfo.uuid;
                } catch (err) {
                    //Nothing to do
                }
    
                try {
                    const osInfo = await sysinfo.osInfo();

                    deviceMeta.client_os = `${osInfo.distro} ${osInfo.release}`;
                } catch (err) {
                    //Nothing to do
                }
            } else if(this.isMobileApp()) {
                deviceMeta.client_serial = window.device.serial,
                deviceMeta.client_uuid = window.device.uuid,
                deviceMeta.client_os = `${window.device.platform} ${window.device.version}`
            }

            this._deviceMeta = deviceMeta;
        }

        return {
            ...this._deviceMeta,
            device_name: window.localStorage.getItem('scl-device-name') || undefined
        };
    }

    public async setDeviceName(deviceName: string) {
        window.localStorage.setItem('scl-device-name', deviceName);

        if(this._deviceMeta) {
            this._deviceMeta.device_name = deviceName;
        }
    }

    /**
     * Returns the session ID.
     * @returns {string} the session ID
     */
    public getSessionId(): string {
        return this._sessionId;
    }

    /**
     * Returns the URL of the PDF document for a given sale.
     *
     * @param {string} saleUuid - The UUID of the sale.
     * @param {string} shopUuid - The UUID of the shop. If not provided, the current shop UUID will be used.
     * @return {string | undefined} The URL of the PDF document or undefined if the shop UUID is not available.
     */
    public getPdfUrl(saleUuid: string, shopUuid?: string): string | undefined {
        const targetShopUuid = shopUuid ?? this.injector.get(UserActiveSessionManagerService).getSession()?.shop.uuid;

        if(!targetShopUuid) {
            return undefined;
        }

        const env = backendEnvMap[EnvironmentConfig.environment];

        return env === 'prod'
            ? `https://er.tilby.com/${env}/${targetShopUuid}/${saleUuid}.pdf`
            : `https://dyi6b1gkcr8yb.cloudfront.net/${env}/${targetShopUuid}/${saleUuid}.pdf`;
    }
}